import { useCallback, useEffect } from 'react';

/** Ceiling on the auto-load, so a busy window can't fire dozens of requests. */
export const DRAIN_CAP = 2_000;

interface DrainInput {
    /** True when a sort or filter is on, so a partial page is no longer enough. */
    active: boolean;
    /** Whether the paginated source still has pages for the current window. */
    hasMore: boolean;
    /** True while a page is in flight. */
    loading: boolean;
    /** The source's error for the current window, if the last page failed. */
    error?: Error | null;
    /** Rows fetched for the CURRENT window; must reset when the window does. */
    loadedForWindow: number;
    /** Fetches the next page. */
    loadMore: () => void;
    cap?: number;
}

export interface DrainState {
    /** The ceiling in force — render the fence message from this, not DRAIN_CAP. */
    cap: number;
    /** Pages are being pulled right now. */
    draining: boolean;
    /** Stopped at the cap with pages still unfetched — the ranking is partial. */
    drainCapped: boolean;
    /** Stopped because a page failed; `retry` resumes. */
    drainFailed: boolean;
    retry: () => void;
}

/**
 * Pulls the rest of a window into memory so a ranking or threshold can be honest
 * about it. Self-driving: it fires on the `loading` false-edge, which a *failed*
 * page also produces — hence the error gate. Termination otherwise rests on the
 * source setting `hasMore` false once its cursor can no longer advance.
 */
export function useTradeDrain({
    active,
    hasMore,
    loading,
    error,
    loadedForWindow,
    loadMore,
    cap = DRAIN_CAP,
}: DrainInput): DrainState {
    const wanted = active && hasMore;
    const drainCapped = wanted && loadedForWindow >= cap;
    const drainFailed = wanted && !drainCapped && !!error;
    const draining = wanted && !drainCapped && !error;

    useEffect(() => {
        if (draining && !loading) loadMore();
    }, [draining, loading, loadMore]);

    const retry = useCallback(() => {
        if (!loading) loadMore();
    }, [loading, loadMore]);

    return { cap, draining, drainCapped, drainFailed, retry };
}
